// src/components/Header.jsx
import React from "react";
import { Link } from "react-router-dom";
import { FaUserCircle } from "react-icons/fa";
import { getAuth } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import logo from "../assets/trapoundtop.png";
import logoSide from "../assets/trapoundside.png";

/**
 * Safely derive the UI chip ("Leader" / "Follower") from an account object.
 * Reads canonical settings at settings.CopyTrade (role + enabled) and
 * gracefully falls back to settings.roleTag if present.
 */
function deriveRoleTag(acc) {
  if (!acc) return null;

  // Prefer canonical nested object
  const ct = acc?.settings?.CopyTrade || acc?.CopyTrade || null;

  // If we have a boolean toggle, respect it
  const enabled = typeof ct?.enabled === "boolean" ? ct.enabled : true;

  // Accept either canonical role ("leader"/"follower") or the convenience tag
  const roleLower = String(ct?.role || "").toLowerCase();
  const tag =
    roleLower === "leader" ? "Leader" :
    roleLower === "follower" ? "Follower" :
    (acc?.settings?.roleTag === "Leader" || acc?.settings?.roleTag === "Follower" ? acc.settings.roleTag : null);

  return enabled ? tag : null;
}

/**
 * Try to get a friendly account name from a Firestore account doc payload.
 */
function getAccountName(acc, fallbackId = "") {
  if (!acc) return fallbackId || "Account";
  return acc.name || acc.accountName || acc?.settings?.name || fallbackId || "Account";
}

/**
 * Header that live-updates the copy-trading role chip as soon as Settings are saved.
 *
 * Props:
 *   - selectedAccountId (string, required)
 *   - userId? (string, optional) — if omitted we fall back to Firebase Auth currentUser.uid
 *   - accounts? (Array<{id, name, settings}>, optional) — used for instant display until the snapshot arrives
 */
export default function Header({ selectedAccountId, userId, accounts }) {
  const [liveAccount, setLiveAccount] = React.useState(null);

  // Use parent-provided accounts array for an instant, optimistic render
  const optimisticAccount = React.useMemo(() => {
    if (!Array.isArray(accounts) || !selectedAccountId) return null;
    return accounts.find(a => a?.id === selectedAccountId) || null;
  }, [accounts, selectedAccountId]);

  // Live Firestore subscription on the selected account
  React.useEffect(() => {
    const uid = userId || getAuth()?.currentUser?.uid;
    if (!uid || !selectedAccountId) {
      setLiveAccount(null);
      return;
    }
    const ref = doc(db, "users", uid, "accounts", selectedAccountId);
    const unsub = onSnapshot(ref, snap => {
      const data = snap.data() || null;
      // Store id + data so getAccountName() has both
      setLiveAccount(data ? { id: selectedAccountId, ...data } : null);
    });
    return () => unsub();
  }, [userId, selectedAccountId]);

  // Prefer the live account, fall back to optimistic view while connecting
  const acc = liveAccount || optimisticAccount;
  const roleTag = deriveRoleTag(acc);
  const accountName = getAccountName(acc, selectedAccountId);

  return (
    <header
      className="relative flex items-center justify-between p-4 border-b bg-white dark:bg-gray-900 dark:border-gray-700 z-30 sticky top-0 left-0 w-full"
      style={{ fontFamily: "'Montserrat', Arial, sans-serif", minHeight: 72, height: 72, overflow: "visible" }}
    >
      {/* Left: Main Logo */}
      <div className="flex items-center">
        <img
          src={logo}
          alt="Trapound Logo"
          className="h-16 w-16 mr-3 -my-4"
          style={{ objectFit: "contain", maxHeight: 96, minHeight: 48 }}
        />
        <span
          style={{
            fontWeight: "bold",
            color: "#1246A5",
            fontSize: "2rem",
            letterSpacing: "1px",
            whiteSpace: "nowrap",
          }}
        />
      </div>

      {/* Center: Side Logo */}
      <div
        className="absolute left-1/2 top-1/2 flex justify-center items-center pointer-events-none"
        style={{ transform: "translate(-50%, -50%)" }}
      >
        <img
          src={logoSide}
          alt="Trapound Side Logo"
          className="h-40 w-auto drop-shadow-lg"
          style={{ maxHeight: 170, minHeight: 60, marginTop: -25, marginBottom: -25, objectFit: "contain" }}
        />
      </div>

      {/* Right: Account link (icon + name + role chip) */}
      <div className="flex items-center">
        <Link
          to="/account"
          aria-label="Go to Account"
          className="group inline-flex items-center gap-2 rounded-lg px-3 py-2 transition
                     hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500
                     dark:hover:bg-gray-800"
          title="Account"
        >
          <FaUserCircle className="text-2xl text-blue-800 dark:text-gray-100 group-hover:opacity-90" />

          {/* Name (only once) */}
          <span
            id="account_name"
            className="text-lg font-semibold text-blue-800 dark:text-gray-100 group-hover:underline"
          >
            {accountName}
          </span>

          {/* Role chip (optional) */}
          {roleTag && (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide
                ${roleTag === "Leader"
                  ? "bg-yellow-500 text-gray-100 dark:bg-yellow-300/90 dark:text-gray-700"
                  : "bg-purple-100 text-purple-700 dark:bg-purple-700/40 dark:text-purple-300"}`}
              title={`Copy trading role: ${roleTag}`}
            >
              {roleTag}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
}
